import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  useUpcoming,
  useCapabilities,
  useCommandStatus,
  useDeviceAction,
  usePolicies,
  useRequestDecision,
  useRequests,
} from "../api/hooks.js";
import { Empty } from "../components/PageState.js";

export function LiveControlsPage({
  deviceId,
  compact = false,
}: {
  deviceId: string;
  compact?: boolean;
}) {
  const [pauseMinutes, setPauseMinutes] = useState("0");
  const [confirmPower, setConfirmPower] = useState(false);
  const powerAction = useDeviceAction(deviceId);
  const [powerId, setPowerId] = useState<string | null>(null);
  const [powerSent, setPowerSent] = useState(0);
  useEffect(() => {
    setConfirmPower(false);
    setPowerId(null);
    setPowerSent(0);
  }, [deviceId]);
  const powerStatus = useCommandStatus(deviceId, powerId);
  const [pauseMessage, setPauseMessage] = useState("");
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const [message, setMessage] = useState("");
  const messageAction = useDeviceAction(deviceId);
  const [messageId, setMessageId] = useState<string | null>(null);
  const messageStatus = useCommandStatus(deviceId, messageId);
  const caps = useCapabilities(deviceId);
  const upcoming = useUpcoming(deviceId);
  const action = useDeviceAction(deviceId);
  const policies = usePolicies(deviceId);
  const requests = useRequests(deviceId);
  const decision = useRequestDecision();
  const [command, setCommand] = useState<{
    id: string;
    kind: string;
    sent: number;
  } | null>(null);
  const status = useCommandStatus(deviceId, command?.id ?? null);
  const cap = caps.data?.find((c) => c.capability === "local_restrictions");
  let report: {
    state?: string;
    restricted_app_count?: number;
    manual_lock?: boolean;
    manual_lock_until_ms?: number;
    checked_at_ms?: number;
    observed_at_ms?: number;
    power_off_available?: boolean;
    daily_allowance?: {
      day: string;
      timezone: string;
      used_ms: number;
      limit_ms: number | null;
      remaining_ms: number | null;
      bonus_ms: number;
      reset_at_ms: number;
      observed_at_ms: number;
    } | null;
    profiles?: { user_id: number; name: string; active: boolean }[];
  } = {};
  try {
    report = JSON.parse(cap?.notes ?? "{}");
  } catch {
    /* No evidence. */
  }
  const fresh =
    !!cap && Date.now() - new Date(cap.checked_at).getTime() < 120000;
  const verified = fresh && report.state === "restrictions_verified";
  const paused = verified && report.manual_lock === true;
  const allowance = report.daily_allowance;
  const allowanceFresh = !!allowance && now - allowance.observed_at_ms < 120000;
  const scheduled = upcoming.data?.current_status;
  const scheduledFresh = !!upcoming.data && now - Date.parse(upcoming.data.generated_at) < 120000;
  const automaticReason = scheduledFresh && scheduled?.confirmed ? scheduled.label.replace(/ active$/, "") :
    verified && allowanceFresh && allowance?.remaining_ms === 0 && (report.restricted_app_count ?? 0) > 0 ? "Daily time used up" : null;
  const playtime = (ms: number) =>
    `${Math.floor(ms / 60000)}m ${Math.floor(ms / 1000) % 60}s`;
  const send = (kind: string, minutes?: number) =>
    action.mutate(
      {
        action: kind,
        body: {
          ...(minutes ? { minutes } : {}),
          ...(kind === "manual-lock" && pauseMessage.trim()
            ? { message: pauseMessage.trim() }
            : {}),
        },
      },
      {
        onSuccess: (r) =>
          setCommand({ id: r.command_id, kind, sent: Date.now() }),
      },
    );
  const confirmed =
    !!command &&
    verified &&
    (report.observed_at_ms ?? report.checked_at_ms ?? 0) > command.sent &&
    ((command.kind === "manual-lock" && report.manual_lock === true) ||
      (command.kind === "unlock" && report.manual_lock === false));
  const failed = ["FAILED", "EXPIRED", "NOT_SUPPORTED"].includes(
    status.data?.state ?? "",
  );
  const resuming = command?.kind === "unlock" && !confirmed && !failed;
  const dailyCap = policies.data?.some(
    (p) => p.enabled && p.type === "DEVICE_PLAYTIME_DAILY",
  );
  if (!deviceId)
    return <Empty label="Add a headset to start managing playtime." />;
  return (
    <>
      {!compact && (
        <section className="card stack" aria-label="Headset power">
          <h3>Power off headset</h3>
          <p className="muted">
            Fully shuts down this headset. Unsaved game progress may be lost.
            Someone must press its power button to turn it back on.
          </p>
          {!fresh || !report.power_off_available ? (
            <p className="notice">
              Unavailable until the updated headset app connects with power-off
              support.
            </p>
          ) : !confirmPower ? (
            <button className="secondary" onClick={() => setConfirmPower(true)}>
              Power off…
            </button>
          ) : (
            <div className="notice stack">
              <strong>Power off the selected headset now?</strong>
              <p>This request expires after one minute if it isn’t received.</p>
              <div className="row">
                <button
                  className="danger-button"
                  disabled={powerAction.isPending}
                  onClick={() =>
                    powerAction.mutate(
                      {
                        action: "power-off",
                        body: { confirmation: "POWER_OFF" },
                      },
                      {
                        onSuccess: (r) => {
                          setPowerId(r.command_id);
                          setPowerSent(Date.now());
                          setConfirmPower(false);
                        },
                      },
                    )
                  }
                >
                  Yes, power off
                </button>
                <button
                  className="secondary"
                  disabled={powerAction.isPending}
                  onClick={() => setConfirmPower(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          {powerAction.error && (
            <p className="notice danger" role="alert">
              Couldn’t send power off. {powerAction.error.message}
            </p>
          )}
          {powerId && (
            <p className="notice" role="status">
              {powerStatus.data?.state === "APPLIED"
                ? "The headset accepted the shutdown request. Going offline alone cannot confirm it powered off."
                : ["FAILED", "NOT_SUPPORTED", "EXPIRED"].includes(
                      powerStatus.data?.state ?? "",
                    )
                  ? "Power off did not complete. Check the headset before trying again."
                  : now - powerSent > 60000
                    ? "The delivery window has ended without confirmation. Check the headset before trying again."
                    : "Power off requested. Waiting for the headset; it must receive this within one minute."}
            </p>
          )}
        </section>
      )}
      <section className="card stack lock-card" aria-label="Device lock">
        <div className="section-title">
          <p className="eyebrow">PLAYTIME CONTROLS</p>
          <span className="badge">Entire headset</span>
        </div>
        <p className="lock-status" role="status">
          {resuming
            ? "Resuming playtime now…"
            : paused
              ? "Playtime is paused"
              : automaticReason
                ? `Playtime is paused · ${automaticReason}`
              : scheduledFresh && scheduled
                ? `${scheduled.label} · waiting for headset confirmation`
              : verified
                ? "Following your playtime rules"
                : "Waiting for the headset"}
        </p>
        <p className="muted">
          {paused
            ? "The headset confirms games and supported entertainment apps are blocked."
            : automaticReason
              ? "The headset confirms app restrictions. This pause comes from a schedule or time limit."
            : verified
              ? "Your schedules, time limits, and blocked games still apply."
              : "We can’t confirm the current restrictions. The headset may be asleep or offline."}
        </p>
        {paused && !resuming && !!report.manual_lock_until_ms && (
          <p className="notice" role="timer">
            {report.manual_lock_until_ms > now
              ? `Resumes in ${Math.ceil((report.manual_lock_until_ms - now) / 60000)} min · ${new Date(report.manual_lock_until_ms).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`
              : "Pause time has ended — waiting for the headset to confirm."}
          </p>
        )}
        {automaticReason && !paused ? <p className="notice">{automaticReason === "Daily time used up" ? "Add extra time below to extend today’s allowance. Other schedules still apply." : <>This scheduled pause ends automatically. To change it, <Link to="/schedules">edit the schedule</Link>.</>}</p> : <>
        <button
          disabled={action.isPending || resuming || (verified && !paused)}
          onClick={() => send("unlock")}
        >
          {resuming
            ? "Waiting for headset confirmation…"
            : "Resume playtime now"}
        </button>
        <p className="muted">
          Resume cancels the current pause immediately when the headset receives
          it. The duration below only applies to a new or updated pause.
        </p>
        </>}
        <label htmlFor={compact ? "pause-home" : "pause-live"}>
          How long should playtime pause?
        </label>
        <select
          id={compact ? "pause-home" : "pause-live"}
          value={pauseMinutes}
          onChange={(e) => setPauseMinutes(e.target.value)}
        >
          <option value="0">Until I resume it</option>
          <option value="15">15 minutes</option>
          <option value="30">30 minutes</option>
          <option value="60">1 hour</option>
          <option value="120">2 hours</option>
        </select>
        <label htmlFor={compact ? "pause-message-home" : "pause-message-live"}>
          Message with this pause (optional)
        </label>
        <textarea
          id={compact ? "pause-message-home" : "pause-message-live"}
          rows={2}
          maxLength={500}
          value={pauseMessage}
          onChange={(e) => setPauseMessage(e.target.value)}
          placeholder="Homework first. We’ll play again after the break."
        />
        <div className="row control-buttons">
          <button
            className="danger-button"
            disabled={action.isPending}
            onClick={() =>
              send("manual-lock", Number(pauseMinutes) || undefined)
            }
          >
            {paused ? "Update pause" : "Pause playtime"}
          </button>
        </div>
        {action.error && (
          <p className="notice danger" role="alert">
            Couldn’t send that change. {action.error.message}
          </p>
        )}
        {command && (
          <div className={`notice ${failed ? "danger" : ""}`} role="status">
            <strong>
              {confirmed
                ? "Confirmed by the headset"
                : failed
                  ? "This change didn’t complete"
                  : command.kind === "bonus-time" &&
                      status.data?.state === "APPLIED"
                    ? "Extra time saved"
                    : "Sent — waiting for the headset"}
            </strong>
            <p>
              {confirmed
                ? command.kind === "manual-lock"
                  ? "Playtime is now paused."
                  : "Manual pause is off. Other rules still apply."
                : failed
                  ? "Check the connection, then try again."
                  : command.kind === "reset-daily-usage"
                    ? status.data?.state === "APPLIED"
                      ? "Today's usage reset was received. The counter will update after the next headset check-in. Bedtime and game limits stay in place."
                      : "Reset requested — waiting for the headset."
                    : command.kind === "bonus-time"
                      ? "Adds to the daily headset limit for 24 hours. Bedtime and blocked games stay in place; headset enforcement is not yet confirmed here."
                      : "Keep the headset online. It checks about every 15 seconds, then applies the change."}
            </p>
            <button className="secondary" onClick={() => setCommand(null)}>
              Dismiss
            </button>
          </div>
        )}
        <details>
          <summary>What does pause block?</summary>
          <p className="muted">
            Games and supported entertainment apps, across detected profiles.
            Quest home, settings and recovery remain accessible. Closing a game
            may lose unsaved progress.
          </p>
        </details>
      </section>
      <section className="card stack" aria-label="Extra time">
        <div>
          <h3>Today’s playtime</h3>
          {allowance ? (
            <div className="stack">
              <strong>
                {playtime(allowance.used_ms)} used ·{" "}
                {allowance.remaining_ms == null
                  ? "No daily headset limit"
                  : `${playtime(allowance.remaining_ms)} remaining`}
              </strong>
              {allowance.limit_ms != null && (
                <>
                  <progress
                    aria-label="Daily allowance used"
                    max={Math.max(allowance.limit_ms, 1)}
                    value={Math.min(allowance.used_ms, allowance.limit_ms)}
                  />
                  <p className="muted">
                    {playtime(allowance.limit_ms)} total allowance ·{" "}
                    {Math.floor(allowance.bonus_ms / 60000)} extra minutes
                    included
                  </p>
                </>
              )}
              <p className="muted">
                {allowance.day} · {allowance.timezone}. Shared across headset
                profiles.{" "}
                {allowanceFresh
                  ? "Updated from the headset’s allowance counter."
                  : "Last reported counter; waiting for the headset to reconnect."}
              </p>
            </div>
          ) : (
            <p className="notice">
              Waiting for today’s counter from the updated headset app.
            </p>
          )}
          <p className="muted">
            Add to the headset’s daily allowance. Bedtime and blocked games stay
            in place.
          </p>
        </div>
        <button
          className="secondary"
          disabled={action.isPending || !allowanceFresh}
          onClick={() => send("reset-daily-usage")}
        >
          Reset today’s used time
        </button>
        <p className="muted">
          Reset restores today’s daily headset allowance. It keeps your limits,
          extra time, bedtime, and blocked games.
        </p>
        <div className="row bonus-options">
          {[15, 30, 60].map((minutes) => (
            <button
              className="secondary"
              key={minutes}
              disabled={action.isPending || !dailyCap}
              onClick={() => send("bonus-time", minutes)}
            >
              +{minutes} min
            </button>
          ))}
        </div>
        {!dailyCap && (
          <p className="muted">
            Set a daily headset limit in Schedules to use extra time.
          </p>
        )}
      </section>
      {!compact && (
        <section className="card stack" aria-label="Send a message">
          <h3>Say it on the headset</h3>
          <p className="muted">
            Send a large popup over their current activity. This doesn’t pause
            playtime or prove they’ve read it.
          </p>
          <label htmlFor="parent-message">Your message</label>
          <textarea
            id="parent-message"
            rows={3}
            maxLength={500}
            placeholder="Dinner’s ready! Please come downstairs."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          <div className="row">
            <button
              disabled={messageAction.isPending || !message.trim()}
              onClick={() =>
                messageAction.mutate(
                  { action: "send-message", body: { message: message.trim() } },
                  {
                    onSuccess: (r) => {
                      setMessageId(r.command_id);
                      setMessage("");
                    },
                  },
                )
              }
            >
              Show message on headset
            </button>
            <span className="muted">{message.length}/500</span>
          </div>
          {messageAction.error && (
            <p className="notice danger" role="alert">
              Couldn’t send the message. Try again.
            </p>
          )}
          {messageId && (
            <p className="notice" role="status">
              {["FAILED", "NOT_SUPPORTED", "EXPIRED"].includes(
                messageStatus.data?.state ?? "",
              )
                ? "Message couldn’t be displayed. Check the headset connection and try again."
                : messageStatus.data?.state === "APPLIED"
                  ? "The headset requested the popup. Read confirmation isn’t available."
                  : "Message sent — waiting for the headset."}
            </p>
          )}
        </section>
      )}
      {!compact && (
        <section className="card stack">
          <h3>Requests for more time</h3>
          {requests.error ? (
            <p className="notice danger">
              Couldn’t load requests. Please try again.
            </p>
          ) : requests.isPending ? (
            <p className="muted">Checking for requests…</p>
          ) : !requests.data?.length ? (
            <div className="empty">
              You’re all caught up.<p>No requests waiting for a decision.</p>
            </div>
          ) : (
            requests.data.map((r) => (
              <div className="notice stack" key={r.id}>
                <strong>{r.requested_minutes} more minutes</strong>
                <p>{r.message || "No message included."}</p>
                <div className="row">
                  <button
                    disabled={decision.isPending}
                    onClick={() =>
                      decision.mutate({ requestId: r.id, approve: true })
                    }
                  >
                    Give extra time
                  </button>
                  <button
                    className="secondary"
                    disabled={decision.isPending}
                    onClick={() =>
                      decision.mutate({ requestId: r.id, approve: false })
                    }
                  >
                    Not this time
                  </button>
                </div>
              </div>
            ))
          )}
          {decision.error && (
            <p className="notice danger" role="alert">
              Couldn’t save your decision. Try again.
            </p>
          )}
          {decision.isSuccess && (
            <p className="notice" role="status">
              Decision saved. Approved extra time will sync when the headset
              connects.
            </p>
          )}
        </section>
      )}
    </>
  );
}
